import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaPaperPlane, FaHistory, FaTrash } from 'react-icons/fa';
import {
    openQuestionApiStreaming,
    getOpenQuestionHistoryApi,
    saveOpenQuestionApi,
    deleteOpenQuestionApi
} from '../../services/api';

const MAX_QUESTION_LENGTH = 45;

const buildPayload = (question, currentQCM, sessionContext) => {
    const metadata = currentQCM?.metadata || {};
    const examCode = sessionContext?.exam || currentQCM?.exam_code || 'general-learning';
    const examLabel = currentQCM?.exam_title || sessionContext?.exam_label || examCode;
    return {
        Domain: metadata.Domain || currentQCM?.domain || 'General',
        topic: metadata.topic || currentQCM?.topic || 'General Topic',
        'Topic_Occurrence_%': metadata['Topic_Occurrence_%'] || 0,
        question: currentQCM?.qcm?.question || currentQCM?.question || 'General context question',
        priority: sessionContext?.priority || 1,
        subject_to_answer: question,
        exam_name: examLabel,
        exam_label: examLabel,
        exam_code: examCode,
        editor_solution: currentQCM?.editor_solution || sessionContext?.editor_solution || 'Learning Context',
        history: [],
        user: sessionContext?.user || 'learner1',
        sessionId: sessionContext?.sessionId || '111222',
        exam_id: examCode,
    };
};

const MobileOpenQuestionScreen = ({
    onClose,
    sessionContext,
    currentQCM,
    initialTab = 'ask'
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [question, setQuestion] = useState('');
    const [error, setError] = useState(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [responses, setResponses] = useState([]);
    const [history, setHistory] = useState([]);
    const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);
    const historyInitializedRef = useRef(false);

    const userId = sessionContext?.user || 'anonymous_user';
    const examId = sessionContext?.exam || currentQCM?.exam_code || 'general-learning';
    const examLabel = useMemo(
        () => currentQCM?.exam_title || sessionContext?.exam_label || examId,
        [currentQCM?.exam_title, sessionContext?.exam_label, examId]
    );

    const loadHistory = useCallback(async () => {
        try {
            const result = await getOpenQuestionHistoryApi(userId, 50, examId);
            if (result?.success) {
                setHistory(result.questions || []);
            } else {
                setHistory([]);
            }
        } catch (err) {
            console.error('MobileOpenQuestionScreen history error:', err);
        }
    }, [userId, examId]);

    useEffect(() => {
        if (!historyInitializedRef.current) {
            historyInitializedRef.current = true;
            loadHistory();
        }
    }, [loadHistory]);

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const updateResponse = useCallback((id, updates) => {
        setResponses(prev => prev.map(entry => (entry.id === id ? { ...entry, ...updates } : entry)));
    }, []);

    const appendResponse = useCallback(entry => {
        setResponses(prev => [entry, ...prev]);
    }, []);

    const removeResponse = useCallback((id) => {
        setResponses(prev => prev.filter(entry => entry.id !== id));
    }, []);

    const persistQuestion = useCallback(async (questionText, answerText) => {
        try {
            const user = sessionContext?.user || 'anonymous';
            const examCode = currentQCM?.exam_code || sessionContext?.exam || 'default-exam';
            return await saveOpenQuestionApi({
                question: questionText,
                answer: answerText,
                user,
                exam_code: examCode,
                exam_name: currentQCM?.exam_title || sessionContext?.exam || 'Default Exam',
                domain: currentQCM?.metadata?.Domain || '',
                topic: currentQCM?.metadata?.topic || ''
            });
        } catch (error) {
            console.error('MobileOpenQuestionScreen save error:', error);
            return null;
        }
    }, [currentQCM, sessionContext]);

    const handleDeleteEntry = useCallback(async (questionId) => {
        if (!questionId) return;
        try {
            await deleteOpenQuestionApi(questionId, userId, examId);
            setHistory(prev => prev.filter(item => item.id !== questionId));
            setResponses(prev => prev.filter(item => item.historyId !== questionId));
            if (selectedHistoryEntry?.id === questionId) {
                setSelectedHistoryEntry(null);
            }
        } catch (err) {
            console.error('MobileOpenQuestionScreen delete error:', err);
            setError(err?.message || 'Unable to delete this entry.');
        }
    }, [userId, examId, selectedHistoryEntry?.id]);

    const handleSubmit = async () => {
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) {
            setError(t('ask_a_question_placeholder', 'Ask an open question to start.'));
            return;
        }
        if (trimmedQuestion.length > MAX_QUESTION_LENGTH) {
            setError(t('question_too_long', 'Question is too long. 45 characters max.'));
            return;
        }

        setError(null);
        setQuestion('');
        setIsStreaming(true);

        const entryId = Date.now();
        const newEntry = {
            id: entryId,
            question: trimmedQuestion,
            answer: '',
            status: 'loading',
            statusMessage: t('generation_in_progress', 'Generating answer...'),
            timestamp: new Date().toISOString()
        };
        appendResponse(newEntry);

        const payload = buildPayload(trimmedQuestion, currentQCM, sessionContext);
        let streamingAnswer = '';

        try {
            await openQuestionApiStreaming(
                payload,
                (data) => {
                    if (data?.type === 'status' || data?.status === 'processing' || data?.status === 'generating') {
                        updateResponse(entryId, { statusMessage: data.message || 'Generating answer...' });
                    } else if (data?.type === 'chunk') {
                        streamingAnswer += `${data.text || ''}`;
                        updateResponse(entryId, {
                            answer: streamingAnswer.trim(),
                            statusMessage: t('generation_in_progress', 'Generating answer...')
                        });
                    }
                },
                (streamError) => {
                    console.error('MobileOpenQuestionScreen streaming error:', streamError);
                    const message = typeof streamError === 'string' ? streamError : (streamError?.message || 'Streaming error');
                    updateResponse(entryId, {
                        status: 'error',
                        statusMessage: message,
                        answer: streamingAnswer.trim()
                    });
                    setError(message);
                    setIsStreaming(false);
                },
                (finalData) => {
                    if (finalData?.success || finalData?.status === 'completed') {
                        const finalAnswer = (finalData?.answer || streamingAnswer || '').trim();
                        updateResponse(entryId, {
                            status: 'done',
                            statusMessage: null,
                            answer: finalAnswer
                        });
                        persistQuestion(trimmedQuestion, finalAnswer).then((saved) => {
                            if (saved?.id) {
                                updateResponse(entryId, { historyId: saved.id });
                            }
                        });
                        loadHistory();
                    } else {
                        const message = finalData?.error || t('generation_failed', 'Unable to generate an answer right now.');
                        updateResponse(entryId, {
                            status: 'error',
                            statusMessage: message,
                            answer: streamingAnswer.trim()
                        });
                        setError(message);
                    }
                    setIsStreaming(false);
                }
            );
        } catch (err) {
            console.error('MobileOpenQuestionScreen submit error:', err);
            const message = err?.message || 'Streaming error';
            updateResponse(entryId, {
                status: 'error',
                statusMessage: message,
                answer: streamingAnswer.trim()
            });
            setError(message);
            setIsStreaming(false);
        }
    };

    const handleHistorySelect = (item) => setSelectedHistoryEntry(item);

    const renderResponseEntry = (entry) => (
        <div key={entry.id} className="bg-gray-800 rounded-xl p-4 space-y-2 border border-gray-700">
            <div className="flex items-start justify-between gap-3">
                <div className="text-sm text-cyan-100 font-semibold">{entry.question}</div>
                <button
                    type="button"
                    onClick={() => {
                        if (entry.historyId) {
                            handleDeleteEntry(entry.historyId);
                        } else {
                            removeResponse(entry.id);
                        }
                    }}
                    className="p-1.5 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                    aria-label={t('delete_entry', 'Delete entry')}
                >
                    <FaTrash className="text-xs" />
                </button>
            </div>
            {entry.answer && (
                <div className="text-sm whitespace-pre-wrap text-gray-200" data-testid="mobile-open-question-answer">
                    {entry.answer}
                </div>
            )}
            {entry.statusMessage && (
                <div className="text-xs text-gray-400 flex items-center gap-2">
                    <FaPaperPlane className="text-gray-500" />
                    <span>{entry.statusMessage}</span>
                </div>
            )}
        </div>
    );

    const renderHistoryEntry = (item) => (
        <div
            key={item.id}
            role="button"
            tabIndex={0}
            onClick={() => handleHistorySelect(item)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleHistorySelect(item);
            }}
            className="w-full text-left bg-gray-800 hover:bg-gray-750 border border-gray-700 rounded-xl p-4 transition-colors cursor-pointer"
        >
            <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-semibold text-cyan-100 line-clamp-2">{item.question}</div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEntry(item.id);
                        }}
                        className="p-1.5 rounded-md text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        aria-label={t('delete_entry', 'Delete entry')}
                    >
                        <FaTrash className="text-xs" />
                    </button>
                    <FaHistory className="text-gray-500" />
                </div>
            </div>
            <div className="text-xs text-gray-400 line-clamp-3 whitespace-pre-wrap">{item.answer}</div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[12000] bg-[#0f172acc] backdrop-blur-md text-white flex flex-col">
            <header className="flex items-center justify-between px-4 py-3 border-b border-slate-700/70 bg-slate-900/55">
                <div className="text-sm text-slate-100 truncate">{examId}</div>
                <button
                    onClick={onClose}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800/80 text-slate-200 hover:text-white hover:bg-slate-700 transition-colors"
                    aria-label={t('close', 'Close')}
                >
                    <FaTimes />
                </button>
            </header>

            {activeTab === 'ask' ? (
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                    <div className="rounded-2xl border border-slate-700/70 bg-slate-900/55 p-4 space-y-3">
                        <div className="text-xs uppercase tracking-widest text-blue-300">Open Search</div>
                        <div className="text-[11px] text-slate-400 truncate">{examLabel}</div>
                        <label className="text-sm text-slate-200" htmlFor="mobile-open-question-input">
                            Ask an open question
                        </label>
                        <textarea
                            id="mobile-open-question-input"
                            value={question}
                            onChange={(e) => {
                                if (e.target.value.length <= MAX_QUESTION_LENGTH) {
                                    setQuestion(e.target.value);
                                    setError(null);
                                }
                            }}
                            placeholder={t('ask_a_question_placeholder', 'e.g. Kendra vs Neptune')}
                            className="w-full rounded-xl border border-slate-700 bg-slate-950/80 px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[84px]"
                            disabled={isStreaming}
                        />
                        <div className="flex items-center justify-between text-xs text-slate-400">
                            <span>{MAX_QUESTION_LENGTH - question.length} {t('characters_left', 'characters left')}</span>
                            {error && <span className="text-red-400">{error}</span>}
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={isStreaming || question.trim().length === 0}
                            className={`w-full flex items-center justify-center gap-2 rounded-xl px-4 py-2 font-semibold transition-colors ${
                                isStreaming
                                    ? 'bg-blue-900/60 text-blue-200 cursor-not-allowed'
                                    : 'bg-blue-600/90 hover:bg-blue-500 text-white'
                            }`}
                        >
                            <FaPaperPlane />
                            {isStreaming ? t('generation_in_progress', 'Generating answer...') : t('send_question', 'Send')}
                        </button>
                    </div>

                    {responses.length > 0 && (
                        <div className="space-y-3">
                            {responses.map(renderResponseEntry)}
                        </div>
                    )}
                </div>
            ) : (
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                    {history.length === 0 ? (
                        <div className="text-center text-sm text-slate-400 py-10">
                            {t('no_history_yet', 'No open-search history yet.')}<br />
                            {t('ask_first_question', 'Use "What is?" from the right menu to add entries.')}
                        </div>
                    ) : (
                        history.map(renderHistoryEntry)
                    )}
                </div>
            )}

            {selectedHistoryEntry && (
                <div className="fixed inset-0 z-[12020] bg-black/65 backdrop-blur-sm flex items-end sm:items-center justify-center p-3">
                    <div className="w-full max-w-xl rounded-2xl border border-slate-700 bg-slate-900 p-4 space-y-3">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-400">Definition</div>
                                <div className="text-sm font-semibold text-cyan-100 mt-1">{selectedHistoryEntry.question}</div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleDeleteEntry(selectedHistoryEntry.id)}
                                    className="p-2 rounded-lg text-slate-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                                    aria-label={t('delete_entry', 'Delete entry')}
                                >
                                    <FaTrash />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSelectedHistoryEntry(null)}
                                    className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
                                    aria-label={t('close', 'Close')}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                        <div className="text-sm text-slate-200 whitespace-pre-wrap">
                            {selectedHistoryEntry.answer || t('no_answer_available', 'No answer available.')}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MobileOpenQuestionScreen;
